/**
 * BloomFilter - A space-efficient probabilistic data structure
 * used to test whether an element is a member of a set.
 */
export class BloomFilter {
  constructor(size = 100000, hashCount = 3) {
    this.size = size;
    this.hashCount = hashCount;
    // Use Uint32Array for better performance and memory efficiency
    this.bitArray = new Uint32Array(Math.ceil(size / 32));
  }

  // A robust FNV-1a hash variant using Math.imul for 32-bit integer multiplication
  _hash(str, seed) {
    let h = (0x811c9dc5 ^ seed) >>> 0;
    for (let i = 0, l = str.length; i < l; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0) % this.size;
  }

  // Add an item to the filter
  add(item) {
    if (!item) return;
    const str = String(item).toLowerCase();
    for (let i = 0; i < this.hashCount; i++) {
      const pos = this._hash(str, i + 1);
      const index = Math.floor(pos / 32);
      const bit = pos % 32;
      this.bitArray[index] |= (1 << bit);
    }
  }

  // Check if an item might be in the filter
  // Returns false if definitely NOT in the set
  // Returns true if PROBABLY in the set
  check(item) {
    if (!item) return false;
    const str = String(item).toLowerCase();
    for (let i = 0; i < this.hashCount; i++) {
      const pos = this._hash(str, i + 1);
      const index = Math.floor(pos / 32);
      const bit = pos % 32;
      if (!(this.bitArray[index] & (1 << bit))) {
        return false;
      }
    }
    return true;
  }

  // Helper to add multiple items at once
  addAll(items) {
    if (Array.isArray(items)) {
      items.forEach(item => this.add(item));
    }
  }
}
